import clsx from 'clsx';

type Step = {
  value: number;
  color?: string;
  className?: string;
  /** Label for accessibility (aria-valuetext) */
  label?: string;
};

type StepWithPercent = Step & { percent: number };

interface BaseProps {
  steps: Step[];
  /** The total value representing 100% */
  total: number;
  className?: string;
  showSeparators?: boolean;
  animationMs?: number;
}

type Props =
  | (BaseProps & {
      'aria-label': string;
      'aria-labelledby'?: never;
    })
  | (BaseProps & {
      'aria-label'?: never;
      'aria-labelledby': string;
    });

export function StackedStatusBar({
  steps,
  total,
  className,
  showSeparators = false,
  animationMs = 600,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: Props) {
  const stackedSteps = steps.reduce<{
    steps: StepWithPercent[];
    valueSum: number;
    percentSum: number;
  }>(
    (acc, step) => {
      if (total <= 0 || acc.valueSum >= total) return acc;

      const remaining = total - acc.valueSum;
      const value = Math.min(step.value, remaining);

      const percent =
        acc.valueSum + value === total
          ? 100 - acc.percentSum
          : Math.floor((value / total) * 100);

      acc.steps.push({ ...step, value, percent });
      acc.valueSum += value;
      acc.percentSum += percent;

      return acc;
    },
    { steps: [], valueSum: 0, percentSum: 0 }
  ).steps;

  const currentTotal = stackedSteps.reduce((s, c) => s + c.value, 0);

  // Construct readable status for screen readers, e.g. "30% Active, 20% Failed"
  const ariaValueText = stackedSteps
    .filter((s) => s.percent > 0)
    .map((s) => `${s.percent}%${s.label ? ` ${s.label}` : ''}`)
    .join(', ');

  const ariaValueMin = total > 0 ? 0 : undefined;
  const ariaValueMax = total > 0 ? total : undefined;
  const ariaValueNow = total > 0 ? currentTotal : undefined;

  return (
    <div
      className={clsx(
        'relative flex h-2.5 w-full overflow-hidden rounded-full bg-gray-3 th-dark:bg-gray-true-3',
        className
      )}
      role="progressbar"
      aria-valuemin={ariaValueMin}
      aria-valuemax={ariaValueMax}
      aria-valuenow={ariaValueNow}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-valuetext={ariaValueText || undefined}
    >
      {stackedSteps.map((step, index) => (
        <div
          key={index}
          className={clsx(
            'h-full transition-all ease-out',
            step.className,
            showSeparators && index !== 0 && 'border-l border-white/60'
          )}
          style={{
            width: `${step.percent}%`,
            backgroundColor: step.color,
            transitionDuration: `${animationMs}ms`,
          }}
        />
      ))}
    </div>
  );
}
